"use client";

import React from "react";
import { ArrowRight, MapPin, Truck, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const markets = [{
  name: "Switzerland",
  type: "HQ & Distribution",
  code: "CH"
}, {
  name: "Serbia",
  type: "Primary Hub",
  code: "RS"
}, {
  name: "Kosovo",
  type: "Regional Market",
  code: "XK"
}, {
  name: "North Macedonia",
  type: "Regional Market",
  code: "MK"
}, {
  name: "Montenegro",
  type: "Regional Market",
  code: "ME"
}, {
  name: "Bosnia & Herzegovina",
  type: "Regional Market",
  code: "BA"
}, {
  name: "Croatia",
  type: "EU Gateway",
  code: "HR"
}, {
  name: "Wider Europe",
  type: "Extended Reach",
  code: "EU"
}];
const capabilities = [{
  title: "Fleet Capacity",
  value: "50+ Heavy Trucks",
  icon: Truck
}, {
  title: "Delivery Time",
  value: "24-48h Regional",
  icon: MapPin
}, {
  title: "Network Reach",
  value: "8+ Primary Markets",
  icon: Globe
}];
export default function EuropeanDistributionNetwork() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="european-distribution-network" className="bg-background text-foreground py-16 md:py-24 relative border-t border-border overflow-hidden">
      {}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/4 -rotate-90 text-[15vw] font-bold text-muted/5 whitespace-nowrap pointer-events-none select-none font-mono">
        LOGISTICS
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-[5vw] relative z-10">
        {}
        <div ref={headerRef} className={cn("mb-12 md:mb-20 flex items-center gap-4 transition-all duration-700 transform", headerVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <span className="font-mono text-xs font-bold tracking-[0.15em] text-primary">
            § 06
          </span>
          <h2 className="font-mono text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">
            Distribution Network
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftColRef} className={cn("lg:col-span-4 border-l border-border pl-6 lg:pl-8 flex flex-col justify-between transition-all duration-700 delay-100 transform", leftColVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
            <div>
              <h3 className="text-3xl md:text-4xl font-medium tracking-tight mb-6 font-default leading-tight">
                Architectural Backbone of the Balkans.
              </h3>
              <p className="text-muted-foreground mb-12 text-lg leading-relaxed">
                Direct sourcing from certified manufacturers delivered through our robust regional logistics network. We ensure competitive pricing and reliable supply chains across Europe.
              </p>

              <div className="space-y-4 mb-12">
                {markets.map((market, i) => <div key={i} data-index={i} className="flex items-center justify-between group border-b border-border/50 pb-3 transition-colors hover:border-primary/50">
                    <div className="flex items-center gap-4">
                      {}
                      <div className="w-1 h-1 bg-primary transition-transform duration-300 group-hover:scale-150" />
                      <span className="font-medium text-foreground/90 group-hover:text-foreground transition-colors">
                        {market.name}
                      </span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">
                      {market.code}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-background hover:text-foreground border border-transparent hover:border-primary transition-all duration-300 group">
                <Link to="/ContactPage">
                  Request Logistics Quote
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightColRef} className={cn("lg:col-span-8 relative transition-all duration-700 delay-200 transform", rightColVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
            {}
            <div className="absolute -top-2 -left-2 text-muted-foreground font-mono text-xs z-20">+</div>
            <div className="absolute -top-2 -right-2 text-muted-foreground font-mono text-xs z-20">+</div>
            <div className="absolute -bottom-2 -left-2 text-muted-foreground font-mono text-xs z-20">+</div>
            <div className="absolute -bottom-2 -right-2 text-muted-foreground font-mono text-xs z-20">+</div>

            <div className="relative aspect-square md:aspect-[16/10] border border-border bg-muted/5 overflow-hidden group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_logistics_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_logistics_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_logistics_map.png-wvc-sizes" alt="European Distribution Network Map" className="w-full h-full object-cover opacity-60 mix-blend-luminosity group-hover:mix-blend-normal group-hover:opacity-80 transition-all duration-1000 scale-105 group-hover:scale-100" loading="lazy" />
              
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20 pointer-events-none" />
              
              {}
              <div className="absolute inset-0 bg-secondary/20 mix-blend-multiply pointer-events-none transition-opacity duration-700 group-hover:opacity-0" />

              {}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gradient-to-t from-background/90 to-transparent">
                {capabilities.map((cap, i) => {
                const Icon = cap.icon;
                return <div key={i} data-index={i} className="bg-background/80 backdrop-blur-md border border-border p-4 transition-colors hover:border-primary/50">
                      <div className="flex items-center gap-3 mb-3">
                        <Icon className="size-4 text-primary" />
                        <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
                          {cap.title}
                        </div>
                      </div>
                      <div className="font-medium text-sm text-foreground">
                        {cap.value}
                      </div>
                    </div>;
              })}
              </div>

              {}
              <div className="absolute top-6 right-6 bg-background/80 backdrop-blur-md border border-border px-3 py-1.5 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-primary animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-foreground">
                  Network Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}